import { ReactElement, type ReactNode } from "react";

import { Box, IconButton, Tab, Tabs } from "@mui/material";

import { CaretDoubleLeftIcon, CaretDoubleRightIcon, ChatsCircleIcon, NoteIcon, SparkleIcon } from "@phosphor-icons/react/dist/ssr";

import useLesson from "@/contexts/useLesson";
import useConversation from "@/hooks/useConversation";
import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { UserRole } from "@/types";

import { ActiveTab, SidebarState } from "./Sidebar";

interface SidebarTabsProps {
  lessonType: string;
  activeTab: ActiveTab;
  setActiveTab: (tab: ActiveTab) => void;
  onToggleSidebar?: () => void;
  sidebarState: SidebarState;
  setSidebarState: (sidebarState: SidebarState) => void;
  isReadOnly: boolean;
}

function SidebarTabs({
  lessonType,
  activeTab,
  setActiveTab,
  onToggleSidebar,
  sidebarState,
  setSidebarState,
  isReadOnly,
}: SidebarTabsProps) {
  const { lessonResourceState, lessonInteractionProfile } = useLesson();
  const { userRole } = useFirebaseContext();
  const { unreadMessagesCount: conversationUnread } = useConversation();
  const unreadMessagesCount = isReadOnly ? 0 : conversationUnread;

  const tabs: { id: ActiveTab; label: string; icon: ReactNode }[] = [
    {
      id: ActiveTab.Chat,
      label: "Chat",
      icon: <ChatsCircleIcon size={16} weight="fill" />,
    },
    {
      id: ActiveTab.Notes,
      label: "Notes",
      icon: <NoteIcon size={16} weight="fill" />,
    },
    ...(lessonType !== "guest" ? [
      {
        id: ActiveTab.Luna,
        label: "Luna",
        icon: <SparkleIcon size={16} weight="fill" />,
      },
    ] : []),
  ];

  const getTabBadge = (tab: ActiveTab) => {
    switch (tab) {
      case ActiveTab.Luna:
        if (userRole === UserRole.Tutor) {
          if (
            lessonInteractionProfile?.realtimeResources?.problems?.some(
              (problem) => !problem.isApproved
            )
          ) {
            return true;
          }
          if (
            lessonInteractionProfile?.realtimeResources?.examples?.some(
              (example) => !example.isApproved
            )
          ) {
            return true;
          }
          if (
            lessonInteractionProfile?.realtimeResources?.problems?.some(
              (problem) => !problem.studentAnswer && problem.isApproved
            )
          ) {
            return true;
          }
        } else {
          const readItems = lessonResourceState?.readItems || [];
          const problems =
            lessonInteractionProfile?.realtimeResources?.problems?.filter(
              (problem) => problem.isApproved && !problem.studentAnswer
            );
          const examples =
            lessonInteractionProfile?.realtimeResources?.examples?.filter(
              (example) => example.isApproved && !readItems.includes(example.id)
            );

          if (examples?.length) return true;
          if (problems?.length) return true;
          return false;
        }
        break;
      case ActiveTab.Chat:
        return unreadMessagesCount > 0;
        break;
      default:
        return false;
    }
  };

  const isSidebarOpen = sidebarState !== SidebarState.COLLAPSED;
  const handleToggleSidebar = () => {
    setSidebarState(isSidebarOpen ? SidebarState.COLLAPSED : SidebarState.STANDARD);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {!isSidebarOpen && (
          <IconButton
            onClick={handleToggleSidebar}
            size="small"
            sx={{
              color: "text.primary",
              p: 1,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "secondary.main",
              mb: 1,
            }}
          >
            <CaretDoubleLeftIcon size={16} weight="bold" />
          </IconButton>
        )}
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            flex: 1,
            width: "100%",
            gap: 2,
            overflow: "hidden",
            maxHeight: isSidebarOpen ? 160 : 0,
            transition: theme.transitions.create("max-height", {
              duration: theme.transitions.duration.standard,
              easing: theme.transitions.easing.easeInOut,
            }),
          })}
        >
          <Box
            onClick={handleToggleSidebar}
            sx={{
              minWidth: 0,
              borderRadius: 1.5,
              color: "text.primary",
              display: "flex",
              cursor: "pointer",
              border: "1px solid",
              borderColor: "secondary.main",
              boxSizing: "border-box",
              p: 1,
            }}
          >
            <CaretDoubleRightIcon size={16} weight="bold" />
          </Box>
          <Tabs
            value={activeTab}
            onChange={(_event, newValue) => setActiveTab(newValue as ActiveTab)}
            indicatorColor="secondary"
            variant="fullWidth"

            sx={{
              flex: 1,
              minWidth: 0,
              width: "100%",
              "& .MuiTab-root": {
                flex: 1,
                alignItems: "center",
                maxWidth: "none",
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.id} label={tab.label} icon={tab.icon as ReactElement} sx={{
                color: activeTab === tab.id ? "text.primary" : "text.secondary",
              }} iconPosition="start" />
            ))}
          </Tabs>
        </Box>
      </Box>
      {!isSidebarOpen && (
        <Box
          className="tabs-icons"
          sx={{
            display: "flex",
            gap: 0.5,
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {tabs.map((tab) => (
            <IconButton
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (!isSidebarOpen) {
                  onToggleSidebar?.();
                }
              }}
              type="button"
              size="small"
              sx={{
                color: "text.primary",
                p: 1,
                borderRadius: 1.5,
                position: "relative",
              }}
            >
              <Box className="tab-icon" sx={{ display: "flex" }}>
                {tab.icon}
              </Box>
              {getTabBadge(tab.id) && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    backgroundColor: "#3b82f6",
                    borderRadius: "50%",
                    position: "absolute",
                    top: 8,
                    right: 8,
                  }}
                />
              )}
            </IconButton>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default SidebarTabs;
